import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FlaskConical, Mail, MessageCircle, Phone, Printer, ShieldCheck } from "lucide-react";
import { getProduct } from "@/lib/api";
import { btnPrimary, SITE } from "@/lib/site";
import usePageMeta from "@/hooks/usePageMeta";

export default function ProductSpec() {
  const { slug } = useParams();
  const printed = useRef(false);
  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => getProduct(slug),
  });

  usePageMeta(
    product ? `Spec Sheet — ${product.name} | ${SITE.name}` : "Spec Sheet | Krithish Scientific Company",
    product ? `Technical specification sheet for ${product.name} — ${product.tagline}` : "Product specification sheet."
  );

  useEffect(() => {
    if (!product || printed.current) return;
    printed.current = true;
    const t = setTimeout(() => window.print(), 1000);
    return () => clearTimeout(t);
  }, [product]);

  if (isLoading)
    return (
      <div className="min-h-[60vh] bg-slate-100">
        <div className="mx-auto max-w-3xl px-4 py-16"><div className="h-[800px] animate-pulse rounded-lg bg-slate-200/70" /></div>
      </div>
    );

  if (isError || !product)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900">Spec sheet not found</h1>
        <Link to="/products" className="mt-6 rounded-lg bg-safety px-6 py-3 text-sm font-semibold text-white hover:bg-safety-dark">Back to products</Link>
      </div>
    );

  const modelCode = `KSC-${product.slug.replace(/-/g, "").slice(0, 2).toUpperCase()}-100`;

  return (
    <div className="-mt-[104px] bg-slate-100 pt-[128px] lg:-mt-[112px] lg:pt-[136px] print:bg-white print:pt-0">
      <div className="mx-auto max-w-3xl px-4 py-8 print:p-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <Link to={`/products/${slug}`} data-testid="spec-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-safety">
            <ArrowLeft size={16} /> Back to product
          </Link>
          <button onClick={() => window.print()} data-testid="spec-print-btn" className={btnPrimary}>
            <Printer size={16} /> Print / Save as PDF
          </button>
        </div>

        <article className="bg-white p-8 shadow-xl sm:p-12 print:shadow-none" data-testid="spec-sheet">
          {/* Letterhead */}
          <header className="flex items-start justify-between gap-4 border-b-2 border-navy pb-6">
            <div className="flex items-center gap-3">
              <img src="/img/ksc/logo.jpg" alt="KSC logo" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
              <div>
                <p className="font-display text-xl font-extrabold leading-none tracking-tight text-slate-900">
                  KRITHISH <span className="text-safety">SCIENTIFIC COMPANY</span>
                </p>
                <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">
                  Precision in Every Scale · Est. 2006
                </p>
              </div>
            </div>
            <div className="text-right font-mono text-[10px] leading-relaxed text-slate-500">
              <p className="flex items-center justify-end gap-1"><ShieldCheck size={11} className="text-safety" /> GSTIN 33AINPR0146B1ZM</p>
              <p>Est. 2006 · Chennai</p>
              <p>{SITE.email}</p>
            </div>
          </header>

          {/* Title */}
          <div className="mt-8">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-safety">
              Specification Sheet · {modelCode}
            </p>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900">{product.name}</h1>
            <p className="mt-2 text-sm text-slate-600">{product.tagline}</p>
          </div>

          <img src={product.image} alt={product.name} className="mt-6 h-64 w-full rounded-lg object-cover" />

          {/* Overview */}
          <h2 className="mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Overview</h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-700">
            {product.description.map((d, i) => (
              <p key={i}>{d}</p>
            ))}
          </div>

          {/* Specs */}
          {product.specs?.length > 0 && (
            <>
              <h2 className="mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Technical Specifications</h2>
              <table className="mt-3 w-full border border-slate-300 text-sm">
                <tbody>
                  {product.specs.map((s) => (
                    <tr key={s.label} className="border-b border-slate-200 last:border-0">
                      <th className="w-2/5 border-r border-slate-200 bg-slate-50 px-4 py-2.5 text-left align-top font-mono text-[11px] font-medium uppercase tracking-wider text-slate-500 print:bg-slate-50">
                        {s.label}
                      </th>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-slate-900">{s.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Applications */}
          <h2 className="mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Application Areas</h2>
          <ul className="mt-3 grid grid-cols-1 gap-1.5 text-sm text-slate-700 sm:grid-cols-2">
            {product.applications.map((a) => (
              <li key={a} className="flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rotate-45 bg-safety" /> {a}
              </li>
            ))}
          </ul>

          {/* Contact strip */}
          <div className="mt-10 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-navy px-6 py-4 text-white">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-rose-300">Place an order or ask for price</p>
            <div className="flex flex-wrap items-center gap-4 font-mono text-[11px]">
              <span className="flex items-center gap-1.5"><Phone size={12} className="text-rose-400" /> {SITE.phoneDisplay}</span>
              <span className="flex items-center gap-1.5"><Mail size={12} className="text-rose-400" /> {SITE.email}</span>
              <span className="flex items-center gap-1.5"><MessageCircle size={12} className="text-rose-400" /> WhatsApp</span>
            </div>
          </div>
          <p className="mt-4 text-[10px] text-slate-400">
            Specifications are indicative and subject to change without notice. Generated from the {SITE.name} product catalog.
          </p>
        </article>
      </div>
    </div>
  );
}
